import type { ToolSlug } from './tools';

export const toolFaqSupplements: Partial<Record<ToolSlug, { question: string; answer: string }[]>> = {
  "enhance": [
    {
      "question": "What is the best source image for AI enhancement?",
      "answer": "Use the highest-resolution, least-compressed original you can access. Camera originals, full-size exports and cloud files usually contain more real texture than screenshots or messaging-app copies. A better source lets the enhancer use lower reconstruction strength and produce a more natural result."
    },
    {
      "question": "Should I denoise or enhance first?",
      "answer": "If random grain, colored speckles or compression blocks are the strongest defect, denoise first and then reassess. If the image is generally soft or flat but already clean, Enhance can be the first step. Avoid stacking every tool automatically because each extra reconstruction can change texture."
    },
    {
      "question": "How do I avoid an overprocessed AI photo?",
      "answer": "Start with moderate detail intensity, compare the result with the source at the same zoom, and inspect skin, hair, text and high-contrast edges. Reduce strength when pores become waxy, hair turns into repeated strands or bright halos appear around objects."
    },
    {
      "question": "What output size should I use for web or social media?",
      "answer": "Keep the original dimensions when they already exceed the destination requirement. Use 2× only when the source is genuinely too small for the intended crop or display. Social platforms often resize uploads, so a huge 4× or 8× file may add reconstruction without a visible benefit."
    }
  ],
  "upscale": [
    {
      "question": "Does changing DPI increase image resolution?",
      "answer": "No. Changing a DPI or PPI metadata value does not create new pixels. For print, calculate the required width and height in pixels from the intended physical size and printer guidance, then upscale only if the source does not meet that pixel target."
    },
    {
      "question": "Should I clean noise before upscaling?",
      "answer": "Usually yes when noise or JPEG artifacts are clearly visible. Enlarging first makes those defects bigger and can cause the upscaler to treat them as texture. Denoise or repair the source, then perform the final upscale from the cleaned master."
    },
    {
      "question": "Can I upscale the same image more than once?",
      "answer": "You can, but repeated 2× passes are usually less predictable than returning to the original and choosing the final multiplier once. Each pass can amplify previous reconstruction and compression, so plan the target size before processing."
    },
    {
      "question": "How large can an upscaled image become?",
      "answer": "The practical limit depends on source megapixels, selected multiplier, plan limits and server processing constraints. The workspace estimates cost and blocks combinations that exceed supported output limits before the job is submitted."
    }
  ],
  "restore": [
    {
      "question": "Should I scan an old photo before restoration?",
      "answer": "Yes. Create the best digital capture first, ideally including an untouched archival scan. A higher-quality scan preserves real facial detail, handwriting and paper information that no restoration model can reliably recreate after it has been lost."
    },
    {
      "question": "Can AI restore missing parts of an old photo exactly?",
      "answer": "No. When scratches, tears or fading have removed information, AI can estimate plausible content but cannot prove what the original pixels contained. Keep the raw scan and treat strong reconstruction as a restored interpretation rather than new historical evidence."
    },
    {
      "question": "Should color correction happen before face restoration?",
      "answer": "Usually correct broad exposure, color cast and physical damage first, then use face restoration only where needed. This makes it easier to judge identity and prevents facial reconstruction from compensating for a poor scan or severe global color problem."
    },
    {
      "question": "How should I save an important restored family photo?",
      "answer": "Keep at least three files: the untouched scan, a high-quality restored master and smaller sharing copies. Store the archival scan and restored master in more than one location and label generated or colorized versions clearly."
    }
  ],
  "face-restore": [
    {
      "question": "When should I use face restoration instead of general enhancement?",
      "answer": "Use face restoration when a face is genuinely tiny, damaged, blurry or heavily compressed. A modern portrait that already has clear eyes and skin detail usually benefits more from mild enhancement, denoise or lighting correction than strong generative face reconstruction."
    },
    {
      "question": "Can face restoration change someone’s identity?",
      "answer": "Strong reconstruction can alter eye shape, teeth, wrinkles, hairline, facial hair, makeup or expression. Compare the result side by side with the source and another verified photo when identity accuracy matters, and prefer modest improvement over dramatic reconstruction."
    },
    {
      "question": "Is face restoration suitable for IDs or legal evidence?",
      "answer": "Use extreme caution. Generated facial detail is an estimate and should not be treated as captured evidence or used to establish identity. Preserve the original and rely on authoritative source material for legal, security or identification decisions."
    },
    {
      "question": "How can I keep skin texture natural?",
      "answer": "Avoid maximum reconstruction on already-clean portraits. Inspect pores, beard stubble and fine lines at 100% zoom, then judge the image again at normal viewing size. Natural skin usually retains some texture rather than becoming perfectly smooth."
    }
  ],
  "remove-background": [
    {
      "question": "Why does my cutout have a white or dark halo?",
      "answer": "Edge pixels can contain color from the original background or from a matte used during export. Test the transparent result on both light and dark backgrounds, then refine color contamination or semi-transparent edges instead of shrinking the whole mask."
    },
    {
      "question": "Can background removal preserve hair and fur?",
      "answer": "It can preserve fine edges when the source has enough separation and focus, but flyaway hair, fur, smoke and translucent materials are difficult. Use a high-resolution source and inspect the alpha edge at 100% zoom on contrasting backgrounds."
    },
    {
      "question": "Should product shadows be removed too?",
      "answer": "Keep a natural contact shadow when it makes sense on the intended new surface and helps the product feel grounded. Remove or rebuild shadows that contain the old background color, have a hard crop or conflict with the new lighting direction."
    },
    {
      "question": "Which format keeps a transparent background?",
      "answer": "PNG is the safest widely supported option for transparency and clean graphic edges. WebP can also preserve transparency when the destination supports it. Ordinary JPEG does not contain an alpha channel and will flatten transparent areas."
    }
  ],
  "deblur": [
    {
      "question": "What is the difference between motion blur and soft focus?",
      "answer": "Motion blur usually stretches detail in a direction and camera shake can produce doubled edges. Soft focus produces broader, directionless softness. Deblur is most useful for directional smear, while mild clean softness may need only enhancement or controlled sharpening."
    },
    {
      "question": "Can AI deblur unreadable text exactly?",
      "answer": "Not reliably. If characters are smeared beyond recognition, AI may create plausible-looking strokes that are incorrect. Recover the original document or retake the image when possible, and verify critical names, numbers or codes against another source."
    },
    {
      "question": "Should I sharpen after deblurring?",
      "answer": "A light finishing sharpen can help after the main blur is corrected, but strong sharpening may create halos around reconstructed edges. Evaluate at the final viewing size and stop when edges are readable rather than maximally crisp."
    },
    {
      "question": "Can deblur fix every camera-shake photo?",
      "answer": "No. Recovery depends on blur length, subject detail, resolution and whether motion is uniform across the frame. Severe smear can destroy unique information, so the result should be treated as a reconstruction rather than guaranteed recovery."
    }
  ],
  "denoise": [
    {
      "question": "What is the difference between luminance and color noise?",
      "answer": "Luminance noise looks like brightness grain, while chroma noise appears as colored speckles or blotches. Fine luminance texture can sometimes remain natural; colored shadow noise is usually more distracting and can often be reduced more strongly."
    },
    {
      "question": "Will denoising make a photo blurry?",
      "answer": "Too much denoise can smear hair, fabric, skin and fine edges because real texture can resemble noise. Use the lowest strength that removes the distracting grain at the final viewing size, then add only restrained sharpening if the cleaned image needs it."
    },
    {
      "question": "Should I denoise a night photo before brightening it?",
      "answer": "Usually yes when the shadows are visibly noisy. Brightening reveals sensor noise, and upscaling makes it larger. A controlled denoise pass before relighting or enlargement gives later processing a cleaner source."
    },
    {
      "question": "Can denoise remove JPEG compression artifacts too?",
      "answer": "It can reduce some blockiness and mosquito noise, but very strong JPEG damage may also need general enhancement and careful edge review. Always look for a less-compressed original first because artifact removal cannot restore exact discarded information."
    }
  ],
  "sharpen": [
    {
      "question": "What does image sharpening actually do?",
      "answer": "Sharpening increases local contrast around existing edges so they appear crisper. It does not uniquely reconstruct a subject that was strongly motion-blurred or out of focus, so use deblur or a better source when the underlying edge information is missing."
    },
    {
      "question": "How do I know if an image is over-sharpened?",
      "answer": "Look for bright or dark halos, gritty skin, emphasized noise and brittle-looking hair or foliage. Check at 100% zoom and at normal output size; a file that looks dramatic when zoomed out may be harsh in print or on a large display."
    },
    {
      "question": "Should sharpening happen before or after upscaling?",
      "answer": "Use only necessary cleanup before upscaling and apply final sharpening after the image reaches its delivery dimensions. The resize or reconstruction changes edge structure, so finishing at the final size gives you better control."
    },
    {
      "question": "Is sharpening useful for screenshots and text?",
      "answer": "It can improve already-readable strokes, but it can also turn ambiguous characters into crisp-looking mistakes when combined with generative enhancement. Retrieve the original document when possible and verify important text independently."
    }
  ],
  "relight": [
    {
      "question": "Can AI relighting brighten only the subject?",
      "answer": "Relighting can emphasize a subject, but light affects the surrounding scene, shadows and reflections too. The most believable result uses a direction and softness consistent with existing geometry rather than simply raising the whole image brightness."
    },
    {
      "question": "How do I describe natural studio lighting?",
      "answer": "Describe direction, softness and temperature: for example, “soft neutral key light from the front-left with gentle fill and natural shadows.” Specific physical descriptions are more controllable than vague prompts such as “make it professional.”"
    },
    {
      "question": "Can relighting change product color?",
      "answer": "Yes. Warm, cool or dramatic lighting can alter perceived color and surface material. For e-commerce, compare the result with the real item or an accurate reference and avoid creative lighting on factual primary listing images."
    },
    {
      "question": "Should I denoise before relighting a dark photo?",
      "answer": "If deep shadows contain visible grain or colored speckles, denoise first. Lifting those shadows can make noise much more obvious, so cleaning the source before major tonal changes usually produces a more natural result."
    }
  ],
  "product-photo": [
    {
      "question": "What should I check after AI product enhancement?",
      "answer": "Verify product color, shape, labels, logos, stitching, ports, texture and any condition details. The image can look cleaner while still being inaccurate, so compare against the original and the physical item before publishing."
    },
    {
      "question": "Should I remove the background before enhancing a product?",
      "answer": "Remove or clean the background first when it is distracting, casts unwanted color onto the subject or must be transparent for reuse. If the source already has a clean consistent background, enhancement can be performed without changing it."
    },
    {
      "question": "How do I keep a product catalog consistent?",
      "answer": "Standardize canvas ratio, subject scale, margins, background treatment and lighting direction for comparable items. Save a clean master for each product, then export marketplace-specific sizes rather than editing previously compressed listing images."
    },
    {
      "question": "Can I use creative relighting for the main marketplace image?",
      "answer": "Use creative lighting for campaigns or secondary lifestyle images when appropriate. Primary listing images should prioritize factual representation and must follow the current requirements of the destination marketplace, so verify its rules before publishing."
    }
  ]
};
